import { Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { IOnboardingState } from './onboarding.state';
import {
  CHEMOTHERAPY_SERVICE,
  IChemotherapyService,
  IOnboardingService,
  IQrCodeService,
  ITermsAndConditionsService,
  IUserService,
  ONBOARDING_SERVICE,
  QR_CODE_SERVICE,
  TERMS_AND_CONDITIONS_SERVICE,
  USER_SERVICE,
} from '@mobile-data-access-services';
import { Store } from '@ngrx/store';
import {
  IRootState,
  PaxmanActions,
  UserActions,
} from '@mobile-data-access-stores';
import {
  catchError,
  delay,
  EMPTY,
  finalize,
  firstValueFrom, from,
  interval,
  mergeMap,
  of,
  pipe,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  takeWhile,
  tap
} from 'rxjs';
import {
  IOnboardingFinish,
  IOnboardingQrCodeContext,
} from '@mobile-data-access-interfaces';
import {
  OnboardingBeginSteps,
  OnboardingRequestHeaderAttributes,
  OnboardingStatuses,
  OnboardingSteps,
  SpinnerContainerIds,
  SystemMessageCodes,
  TimeSpans
} from '@mobile-data-access-enums';
import {
  ISmartNavigatorService,
  ISpinnerService,
  SMART_NAVIGATOR_SERVICE,
  SPINNER_SERVICE,
} from '@ui-tool/core';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import {
  CloseWebviewNativeMethod,
  DashboardNavigationRequest,
} from '@mobile-data-access-models';
import { IRpcService, RPC_SERVICE } from '@message-bus/core';

const MANUAL_ONBOARDING_REQUIRED = 'MANUAL_ONBOARDING_REQUIRED';
const MANUAL_ONBOARDING_REQUIRED_STORAGE_KEY =
  'MANUAL_ONBOARDING_REQUIRED_STORAGE_KEY';

@Injectable()
export class OnboardingComponentStore extends ComponentStore<IOnboardingState> {
  //#region Constructor
  public override  destroy$ = new Subject<void>();
  public readonly TimeSpans = TimeSpans;
  private __intervalSubscription?: Subscription;

  public ionViewDidEnter(): void {
    this.destroy$ = new Subject<void>();
  }
  //#region Effects
  public readonly finishAsync = this.effect<{
    data: IOnboardingFinish;
    cb: () => void;
  }>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(({ data, cb }) =>
        this._onboardingService.finishAsync(data).pipe(
          delay(400),
          tap(() => {
            sessionStorage.removeItem(
              OnboardingRequestHeaderAttributes.X_PIN_CODE
            );
            this.__updateManualOnboardingRequired(false);
            this.updateValue('loading', false);
          }),
          switchMap(() => {
            return this._userService.getProfileAsync().pipe(
              tap((profile) => {
                this._store.dispatch(
                  PaxmanActions.saveActivation({
                    data: profile.activatedPaxman,
                  })
                );
                this._store.dispatch(
                  UserActions.saveProfile({ data: profile })
                );
              }),
              switchMap(() => {
                return this._navigationService
                  .navigateToScreenAsync(new DashboardNavigationRequest())
                  .pipe(tap(() => cb()));
              })
            );
          }),
          catchError((err) => {
            console.error(err);
            const errorMessage = 'No disease data was found for your selection';
            this.showError(errorMessage, '').then();
            this.updateValue('loading', false);
            return EMPTY;
          })
        )
      )
    )
  );

  public readonly loadTermsAndConditions = this.effect<never>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(() => {
        return this._termsAndConditionsService
          .getTermsAndConditionsAsync()
          .pipe(
            tap((e) => this.updateValue('termsAndConditions', e)),
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
            finalize(() => {
              this.updateValue('loading', false);
            })
          );
      })
    )
  );
  //#endregion

  //#region Constructor
  public readonly acceptTermsAndConditions = this.effect<
    { manualOnboardingRequiredCb?: () => void } | void
  >(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap((context) => {
        return this._termsAndConditionsService
          .acceptTermsAndConditionsAsync()
          .pipe(
            mergeMap(() => this._userService.getProfileAsync()),
            tap((profile) => {
              this._store.dispatch(
                PaxmanActions.saveActivation({
                  data: profile.activatedPaxman,
                })
              );
              this._store.dispatch(UserActions.saveProfile({ data: profile }));
              this.updateValue('acceptTermCondition', true);
              this.updateValue('data', profile.onboarding);
            }),
            switchMap(() => {
              if (this.state.manualOnboardingRequired) {
                context?.manualOnboardingRequiredCb?.();
                this.loadDiseaseAsync(() =>
                  this.updateValue(
                    'key',
                    OnboardingBeginSteps.CHOOSE_YOUR_DISEASE
                  )
                );
                return of(undefined);
              }

              this.__updateManualOnboardingRequired(false);
              this.updateValue('loading', false);
              return this._navigationService.navigateToScreenAsync(
                new DashboardNavigationRequest()
              );
            }),
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
            finalize(() => this.updateValue('loading', false))
          );
      })
    )
  );

  //#endregion
  public readonly loadDiseaseAsync = this.effect<() => void>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap((cb) =>
        this._onboardingService.getDiseasesAsync().pipe(
          tap((e) => this.updateValue('items', e)),
          tap(() => cb()),
          delay(400),
          tap(() => this.updateValue('loading', false)),
          catchError((err) => {
            this.updateValue('loading', false);
            console.error(err);
            return EMPTY;
          })
        )
      )
    )
  );

  public readonly beginAsync = this.effect<never>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(() =>
        this._onboardingService.beginAsync().pipe(
          tap((beginResponse) => {
            const currentData = this.state.data;
            this.updateValue('data', {
              ...currentData,
              status: OnboardingStatuses.ONBOARDING,
            } as any);

            if (beginResponse?.message === MANUAL_ONBOARDING_REQUIRED) {
              this.__updateManualOnboardingRequired(true);
              this.updateValue('key', OnboardingBeginSteps.ENTER_PIN_CODE);
            } else {
              this.__updateManualOnboardingRequired(false);
              this.updateValue(
                'key',
                OnboardingBeginSteps.TERMS_AND_CONDITIONS
              );
            }
            this.updateValue('loading', false);
          }),
          catchError(() => {
            this.updateValue('key', OnboardingBeginSteps.TERMS_AND_CONDITIONS);
            this.updateValue('loading', false);
            return EMPTY;
          })
        )
      )
    )
  );
  public readonly unlockAsync = this.effect<{ data: string; cb: () => void }>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(({ data, cb }) =>
        this._onboardingService.unlockAsync(data).pipe(
          tap(() => {
            cb();
            sessionStorage.setItem(
              OnboardingRequestHeaderAttributes.X_PIN_CODE,
              data
            );
            const isOnboarded =
              this.state.data!.status === OnboardingStatuses.ONBOARDED;

            if (isOnboarded) {
              this.loadDiseaseAsync(() =>
                this.updateValue(
                  'key',
                  OnboardingBeginSteps.CHOOSE_YOUR_DISEASE
                )
              );
              this.updateValue('loading', false);
              return;
            }

            if (
              this.state.manualOnboardingRequired &&
              !this.state.acceptTermCondition
            ) {
              this.updateValue(
                'key',
                OnboardingBeginSteps.TERMS_AND_CONDITIONS
              );
              this.updateValue('loading', false);
              return;
            }

            if (this.state.acceptTermCondition) {
              this.loadDiseaseAsync(() =>
                this.updateValue(
                  'key',
                  OnboardingBeginSteps.CHOOSE_YOUR_DISEASE
                )
              );
              this.updateValue('loading', false);
              return;
            }

            this.updateValue(
              'key',
              OnboardingBeginSteps.TERMS_AND_CONDITIONS
            );
            this.updateValue('loading', false);
          }),
          catchError((exception) => {
            this.updateValue('loading', false);
            cb();

            const asyncHandler = async () => {
              if (
                exception?.error?.code === 'TOO_MANY_PIN_CODE_INPUT' &&
                exception?.error?.meta
              ) {
                this.__stopCountDown();
                const waitTime = new Date(exception.error.meta.waitTime);
                let waitTimeInMinutes = (waitTime.getTime() - Date.now()) / TimeSpans.MINUTE_TO_MILLISECOND;

                const updateErrorMessageAsync = async () => {
                  if (waitTimeInMinutes > 0) {
                    const errorTransloco = await firstValueFrom(
                      this._translocoService.selectTranslate(
                        'MESSAGE_ERROR_PIN_CODE',
                        { nextRetryTime: `1 minute` },
                        { scope: 'ONBOARDING_PAGE' }
                      )
                    )
                    this.updateValue('invalidPinCodeMessage', `${errorTransloco}`);
                  } else {
                    this.updateValue('invalidPinCodeMessage', '');
                  }
                }
                await updateErrorMessageAsync();
                if (waitTimeInMinutes > 0) {
                  this.__intervalSubscription = interval(TimeSpans.MINUTE_TO_MILLISECOND).pipe(
                    takeWhile(() => waitTimeInMinutes > 0),
                    takeUntil(this.destroy$),
                    mergeMap(() => {
                      waitTimeInMinutes = Math.max(0, (waitTime.getTime() - Date.now()) / TimeSpans.MINUTE_TO_MILLISECOND);
                      return from(updateErrorMessageAsync());
                    })
                  ).subscribe();
                }
              } else {
                await this.showError('ERROR_WRONG_PIN', 'ONBOARDING_PAGE');
              }
            }

            return from(asyncHandler())
              .pipe(
                mergeMap(() => EMPTY)
              );
          })
        )
      )
    )
  );
  public readonly validPilCodeAsync = this.effect<{
    pilCode: string;
    cb: (data: string) => void;
  }>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(({ pilCode, cb }) =>
        this._chemotherapyService.getByPilCodeAsync(pilCode).pipe(
          tap((data) => cb(data)),
          catchError(async (err) => {
            await this.showError('ERROR_WRONG_PIL', 'ONBOARDING_PAGE');
            console.error(err);
            return EMPTY;
          }),
          finalize(() => this.updateValue('loading', false))
        )
      )
    )
  );
  public readonly loadItemByDiseaseAsync = this.effect<{
    key: string;
    ids: string[];
    cb: () => void;
  }>(
    pipe(
      tap(() => this.updateValue('loading', true)),
      switchMap(({ key, ids, cb }) =>
        this._onboardingService.getItemByDiseaseAsync(key, ids).pipe(
          tap((e) => this.updateValue('items', e)),
          tap(() => cb()),
          delay(400),
          tap(() => this.updateValue('loading', false)),
          catchError((err) => {
            this.updateValue('loading', false);
            console.error(err);
            return EMPTY;
          })
        )
      )
    )
  );

  public readonly scanQrCode = this.effect<IOnboardingQrCodeContext>(
    pipe(
      switchMap((context) => {
        const displaySpinnerRequestId = this._spinnerService.displaySpinner(
          SpinnerContainerIds.APPLICATION
        );
        return this._qrCodeService.captureAsync().pipe(
          tap((code) => {
            if (context.resultHandler) {
              context.resultHandler(
                true,
                SystemMessageCodes.QR_SCANNED_SUCCESSFULLY,
                code
              );
            }

            this._spinnerService.deleteSpinner(
              SpinnerContainerIds.APPLICATION,
              displaySpinnerRequestId
            );
          }),
          catchError((exception) => {
            if (context.resultHandler) {
              context.resultHandler(false, `${exception?.messageCode}`);
            }
            this._spinnerService.deleteSpinner(
              SpinnerContainerIds.APPLICATION,
              displaySpinnerRequestId
            );
            return of(void 0);
          })
        );
      })
    )
  );

  public async showError(key: string, scope: string): Promise<void> {
    const toastInstance = await this._toastController.create({
      color: 'danger',
      position: 'top',
      duration: 5000,
    });
    toastInstance.message = await firstValueFrom(
      this._translocoService.selectTranslate(key, {}, { scope })
    );
    await toastInstance.present();
  }

  public constructor(
    @Inject(ONBOARDING_SERVICE)
    protected readonly _onboardingService: IOnboardingService,
    @Inject(CHEMOTHERAPY_SERVICE)
    protected readonly _chemotherapyService: IChemotherapyService,
    @Inject(USER_SERVICE)
    protected readonly _userService: IUserService,
    protected readonly _store: Store<IRootState>,
    @Inject(QR_CODE_SERVICE)
    protected readonly _qrCodeService: IQrCodeService,
    @Inject(SMART_NAVIGATOR_SERVICE)
    protected readonly _navigationService: ISmartNavigatorService,
    protected readonly _toastController: ToastController,
    protected readonly _translocoService: TranslocoService,
    @Inject(RPC_SERVICE) protected readonly _rpcService: IRpcService,
    @Inject(TERMS_AND_CONDITIONS_SERVICE)
    protected readonly _termsAndConditionsService: ITermsAndConditionsService,
    @Inject(SPINNER_SERVICE) protected readonly _spinnerService: ISpinnerService
  ) {
    super({
      loading: false,
      key: OnboardingSteps.LANDING_PAGE,
      items: [],
      data: null,
      termsAndConditions: null,
      acceptTermCondition: false,
      manualOnboardingRequired:
        sessionStorage.getItem(MANUAL_ONBOARDING_REQUIRED_STORAGE_KEY) ===
        'true',
      selectedDiseaseIds: [],
      invalidPinCodeMessage: null,
    });
  }


  public async closeWebView(): Promise<void> {
    const displaySpinnerRequestId = this._spinnerService.displaySpinner(
      SpinnerContainerIds.APPLICATION
    );

    try {
      await firstValueFrom(
        this._rpcService.sendRequestAsync(
          new CloseWebviewNativeMethod(),
          5 * 1000
        )
      );
    } catch (exception) {
      this._spinnerService.deleteSpinner(
        SpinnerContainerIds.APPLICATION,
        displaySpinnerRequestId
      );

      const toastInstance = await this._toastController.create({
        color: 'danger',
        position: 'top',
        duration: 5000,
      });
      toastInstance.message = await firstValueFrom(
        this._translocoService.selectTranslate(
          'SOMETHING_WENT_WRONG_WHILE_CLOSING_WEB_VIEW',
          {},
          { scope: 'SYSTEM_MESSAGE' }
        )
      );
      await toastInstance.present();
    } finally {
      this._spinnerService.deleteSpinner(
        SpinnerContainerIds.APPLICATION,
        displaySpinnerRequestId
      );
    }
  }

  private __stopCountDown(): void {
    if (!this.__intervalSubscription) {
      return;
    }
    this.__intervalSubscription.unsubscribe();
    this.__intervalSubscription = undefined;
  }

  public ionViewDidLeave(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.__stopCountDown();
  }

  //#endregion

  //#region Settles

  public get state(): IOnboardingState {
    return this.get();
  }

  public updateValue<TKey extends keyof IOnboardingState>(
    key: TKey,
    value: IOnboardingState[TKey]
  ): void {
    const updater = this.updater<IOnboardingState[TKey]>((state, value) => ({
      ...state,
      [key]: value,
    }));

    updater(value);
  }

  private __updateManualOnboardingRequired(value: boolean): void {
    this.updateValue('manualOnboardingRequired', value);

    if (value) {
      sessionStorage.setItem(MANUAL_ONBOARDING_REQUIRED_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(MANUAL_ONBOARDING_REQUIRED_STORAGE_KEY);
    }
  }

  //#endregion
}
